import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { GoArrowLeft, GoArrowRight } from 'react-icons/go'

const WhomIsItFor = ({ currentProgram }) => {
  const { t } = useTranslation()
  const sliderRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)

  const programs = [
    {
      slug: 'full-stack',
      title: t('programs-page.programs-fullstack-title'),
      description: t('programs-page.program-detail-hero-description-full-stack'),
    },
    {
      slug: 'front-end',
      title: t('programs-page.programs-frontend-title'),
      description: t('programs-page.program-detail-hero-description-front-end'),
    },
    {
      slug: 'back-end',
      title: t('programs-page.programs-backend-title'),
      description: t('programs-page.program-detail-hero-description-back-end'),
    },
    {
      slug: 'software-engineering',
      title: t('programs-page.programs-softwareengineering-title'),
      description: t('programs-page.program-detail-hero-description-software-engineering'),
    },
  ]
  const filteredPrograms = currentProgram
    ? programs.filter((program) => program.slug !== currentProgram)
    : programs
  const shouldUseSlider = filteredPrograms.length > 3

  const updateScrollButtons = () => {
    const el = sliderRef.current
    if (!el || !shouldUseSlider) return
    const maxScrollLeft = el.scrollWidth - el.clientWidth
    const left = el.scrollLeft
    setCanScrollLeft(left > 2)
    setCanScrollRight(left < maxScrollLeft - 2)
    setScrollProgress(maxScrollLeft > 0 ? Math.min(1, Math.max(0, left / maxScrollLeft)) : 0)
  }

  useEffect(() => {
    if (!shouldUseSlider) return
    updateScrollButtons()
    const el = sliderRef.current
    if (!el) return
    el.addEventListener('scroll', updateScrollButtons, { passive: true })
    window.addEventListener('resize', updateScrollButtons)
    return () => {
      el.removeEventListener('scroll', updateScrollButtons)
      window.removeEventListener('resize', updateScrollButtons)
    }
  }, [shouldUseSlider])

  const scrollByCards = (direction) => {
    const el = sliderRef.current
    if (!el) return
    const cards = el.querySelectorAll('[data-program-card]')
    if (!cards.length) return
    const positions = Array.from(cards).map((card) => card.offsetLeft)
    const current = el.scrollLeft

    if (direction === 'right') {
      const next = positions.find((pos) => pos > current + 2)
      el.scrollTo({ left: next ?? positions[positions.length - 1], behavior: 'smooth' })
      return
    }

    const previous = [...positions].reverse().find((pos) => pos < current - 2)
    el.scrollTo({ left: previous ?? 0, behavior: 'smooth' })
  }

  return (
    <div className="w-full bg-gradient-to-b from-[#091F59] to-[#1A3884] py-[60px] md:py-[100px] my-[60px] md:my-[100px] rounded-[40px] md:rounded-[64px]">
      <div className="max-w-[90%] mx-auto">
        <h2 className="text-[32px] md:text-[48px] lg:text-[60px] font-[500] text-white mb-[24px]">
          {t('programs-page.programs-title')}
        </h2>
        <div className="relative">
          <div
            ref={sliderRef}
            className={
              shouldUseSlider
                ? 'flex flex-col md:flex-col lg:flex-row gap-[24px] md:gap-[32px] lg:overflow-x-auto lg:scroll-smooth lg:snap-x lg:snap-mandatory pb-[18px] lg:[-ms-overflow-style:none] lg:[scrollbar-width:none] lg:[&::-webkit-scrollbar]:hidden'
                : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[24px] md:gap-[32px]'
            }
          >
            {filteredPrograms.map((program, index) => (
              <div
                key={index}
                data-program-card={shouldUseSlider ? true : undefined}
                className={
                  shouldUseSlider
                    ? 'w-full lg:flex-none lg:w-[400px] xl:w-[480px] lg:snap-start relative rounded-[24px] overflow-hidden bg-[linear-gradient(80.42deg,rgba(255,255,255,0.08)_25.25%,rgba(83,84,108,0.1)_98.05%)] backdrop-blur-[16px]'
                    : 'relative rounded-[24px] overflow-hidden bg-[linear-gradient(80.42deg,rgba(255,255,255,0.08)_25.25%,rgba(83,84,108,0.1)_98.05%)] backdrop-blur-[16px]'
                }
              >
                <div className="absolute inset-0 rounded-[24px] p-[1.2px] pointer-events-none" style={{
                  background: 'linear-gradient(185deg, rgba(29, 54, 118, 0.5) -1.41%, #3A9FDA 85.85%)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                }}></div>
                <div className="px-[16px] py-[40px] flex flex-row gap-[20px] relative">
                  <div className="space-y-[12px] md:space-y-[20px] flex-1 relative z-10">
                    <div>
                      <h3 className="text-[20px] md:text-[24px] font-[600] text-[#CAD1E9] mb-[12px] text-center">
                        {program.title}
                      </h3>
                    </div>
                    <div className="text-[#CAD1E9] text-[14px] md:text-[16px]">
                      {program.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {shouldUseSlider && (
            <div className="hidden lg:flex items-center justify-between gap-[16px] mt-[14px]">
              <div className="flex-1 max-w-[90%] h-[6px] rounded-full bg-[rgba(255,255,255,0.14)] relative overflow-hidden">
                <div
                  className="absolute top-0 bottom-0 w-[50%] rounded-full bg-white"
                  style={{ transform: `translateX(${scrollProgress * 100}%)` }}
                />
              </div>
              <div className="flex items-center gap-[16px]">
                <button
                  type="button"
                  onClick={() => scrollByCards('left')}
                  disabled={!canScrollLeft}
                  className="w-[44px] h-[44px] rounded-full bg-[linear-gradient(80.42deg,rgba(255,255,255,0.08)_25.25%,rgba(83,84,108,0.1)_98.05%)] text-white flex items-center justify-center transition-opacity disabled:opacity-40"
                  aria-label="Scroll left"
                >
                  <GoArrowLeft className="w-[18px] h-[18px]" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollByCards('right')}
                  disabled={!canScrollRight}
                  className="w-[44px] h-[44px] rounded-full bg-[linear-gradient(80.42deg,rgba(255,255,255,0.08)_25.25%,rgba(83,84,108,0.1)_98.05%)] text-white flex items-center justify-center transition-opacity disabled:opacity-40"
                  aria-label="Scroll right"
                >
                  <GoArrowRight className="w-[18px] h-[18px]" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default WhomIsItFor